import { Flex, HStack, IconName, MobileIconButton, Stack, ToolbarButtonWrapper } from '@talos/kyoko';
import styled from 'styled-components';
import {
  MOBILE_SYMBOL_DEEP_DIVE_MODULE_COUNT,
  MOBILE_SYMBOL_DEEP_DIVE_MODULES,
  useMobileSymbolDeepDiveModuleContext,
  type MobileSymbolDeepDiveModuleIndex,
} from './MobileSymbolDeepDiveModuleContext';

const FooterWrapper = styled(Stack)`
  flex-shrink: 0;
  padding-bottom: env(safe-area-inset-bottom);
  background: var(--backgroundContent);
  z-index: 1;
`;

const PageIndicatorButton = styled.button.attrs({ type: 'button' })<{ $isActive: boolean }>`
  width: ${({ theme }) => theme.spacing8}px;
  height: ${({ theme }) => theme.spacing8}px;
  border: 0;
  border-radius: 50%;
  padding: 0;
  background: ${({ $isActive, theme }) => ($isActive ? theme.colorTextImportant : theme.colorTextMuted)};
  cursor: pointer;
`;

export function MobileSymbolDeepDiveFooter() {
  const { moduleIndex, setModuleIndex } = useMobileSymbolDeepDiveModuleContext();

  const goToPrevious = () => {
    setModuleIndex(prev => Math.max(0, prev - 1) as MobileSymbolDeepDiveModuleIndex);
  };

  const goToNext = () => {
    setModuleIndex(
      prev => Math.min(MOBILE_SYMBOL_DEEP_DIVE_MODULE_COUNT - 1, prev + 1) as MobileSymbolDeepDiveModuleIndex
    );
  };

  const goToModule = (index: MobileSymbolDeepDiveModuleIndex) => {
    setModuleIndex(index);
  };

  return (
    <FooterWrapper background="backgroundContent" data-testid="mobile-symbol-deep-dive-footer">
      <HStack
        justifyContent="space-between"
        alignItems="center"
        px="spacing16"
        py="spacing12"
        borderTop="1px solid"
        borderColor="backgroundDivider"
      >
        <MobileIconButton
          data-testid="mobile-symbol-deep-dive-prev-module"
          ghost
          icon={IconName.ArrowLeft}
          disabled={moduleIndex === 0}
          onClick={goToPrevious}
        />
        <Flex gap="spacing6" alignItems="center">
          <ToolbarButtonWrapper h="auto" gap="spacing8" p="spacing8">
            {MOBILE_SYMBOL_DEEP_DIVE_MODULES.map((module, index) => (
              <PageIndicatorButton
                key={module.id}
                $isActive={moduleIndex === index}
                aria-label={`Show ${module.label}`}
                aria-current={moduleIndex === index ? 'page' : undefined}
                onClick={() => goToModule(index as MobileSymbolDeepDiveModuleIndex)}
                data-testid={`mobile-symbol-deep-dive-page-indicator-${index}`}
              />
            ))}
          </ToolbarButtonWrapper>
        </Flex>
        <MobileIconButton
          data-testid="mobile-symbol-deep-dive-next-module"
          ghost
          icon={IconName.ArrowRight}
          disabled={moduleIndex === MOBILE_SYMBOL_DEEP_DIVE_MODULE_COUNT - 1}
          onClick={goToNext}
        />
      </HStack>
    </FooterWrapper>
  );
}
